/**
 * pos-id 治理模組（A 匹配記錄 / B 白名單 / C 變更記錄）前端共用工具。
 *
 * 契約來源：docs/pos-id-frontend-guide-2026-10.md（本檔案所有行為都對應其章節）
 *  - §1.3  `error.code` 才是判據，HTTP 狀態碼只是參考（10060→409、10061→422、其餘業務碼→403）
 *  - §2.1  搜尋框清空時「不要」送 `pos_id=` / `pos_id_exact=`（送空值會回「空集」，這是後端刻意的防全表保護）
 *  - §3.1  白名單的空白規則同 §2.1
 *  - §4.1  變更記錄預設前綴匹配，`pos_id_prefix=0` 切精確
 *
 * 本檔案只放**可獨立驗證的純函式**（不依賴 Vue / axios / store），
 * 供 `scripts/posid-contract-check.mjs` 直接載入斷言；唯一例外是
 * `triggerBlobDownload()`（僅在瀏覽器內被呼叫）。
 */

// ─────────────────────────────────────────────────────────────
// 1. 錯誤碼 → 中文文案（guide §1.3）
// ─────────────────────────────────────────────────────────────

/** `error.code` → 前端中文提示（後端 message 是英文短句，中文由前端按 code 映射） */
export const POS_ID_ERROR_TEXT = {
  10060: '該 pos-id 已匹配到這部影片（可從列表用「精確」模式反查並跳至該筆記錄）',
  10061: '影片與 master 不匹配，請確認 master 是否正確；確定要覆寫時請勾選「強制略過校驗」',
  10010: '記錄 / 影片 / 院線不存在，請重新整理列表後再試',
  10040: '輸入不合法（pos-id 正規化後為空 / 超過 15 字元 / 沒有待改欄位 / 日期非法 / 匯出超過 10 萬行）'
}

/** 沒有業務碼時的 HTTP 兜底文案 */
export const POS_ID_HTTP_FALLBACK_TEXT = {
  400: '請求錯誤',
  401: '登錄已過期，請重新登入',
  403: '沒有操作權限（本模組需要 server-admin 權限）',
  404: '資源不存在或已刪除',
  409: '數據衝突，請稍後重試',
  422: '提交的數據有誤，請檢查後再試',
  500: '服務器錯誤，請稍後重試'
}

/** 無 server-admin 權限時的降級提示（guide §6） */
export const POS_ID_READ_ONLY_HINT =
  '目前帳號沒有 server-admin 權限，本模組僅供檢視：編輯 / 新增 / 停用 / 匯出入口已隱藏。'

/**
 * 把 axios 錯誤正規化成頁面可用的結構。
 * @param {*} error axios 拋出的錯誤（或任何物件）
 * @returns {{code:(number|null),httpStatus:(number|null),backendMessage:string,message:string,
 *   isPermissionDenied:boolean,isUnauthenticated:boolean,isNetworkError:boolean}}
 */
export function resolvePosIdError(error) {
  const response = error && error.response ? error.response : null
  const httpStatus = response && typeof response.status === 'number' ? response.status : null
  const body = response && response.data && typeof response.data === 'object' ? response.data : null
  const errorObj = body && body.error && typeof body.error === 'object' ? body.error : null

  let code = errorObj ? errorObj.code : null
  if (code === undefined || code === null || code === '') {
    code = body ? body.code : null
  }
  if (typeof code === 'string' && /^[0-9]+$/.test(code)) {
    code = Number(code)
  }
  if (typeof code !== 'number') {
    code = null
  }

  const backendMessage =
    (errorObj && errorObj.message) || (body && body.message) || ''
  const mapped = code !== null ? POS_ID_ERROR_TEXT[code] : ''
  const fallback =
    (httpStatus !== null ? POS_ID_HTTP_FALLBACK_TEXT[httpStatus] : '') ||
    '請求失敗，請稍後重試'

  return {
    code,
    httpStatus,
    backendMessage,
    message: mapped || backendMessage || fallback,
    // 403 且沒有業務碼 = 不是 server-admin（guide §6）
    isPermissionDenied: httpStatus === 403 && code === null,
    isUnauthenticated: httpStatus === 401,
    isNetworkError: !response
  }
}

// ─────────────────────────────────────────────────────────────
// 2. 查詢參數組裝（guide §2.1 / §3.1 / §4.1）
// ─────────────────────────────────────────────────────────────

/** 空值判斷：null / undefined / 純空白都算空 */
export function isBlank(value) {
  return (
    value === undefined || value === null || String(value).trim() === ''
  )
}

/** 只在「非空」時才寫入 key —— 這正是「清空搜尋框不要送空值」的實作 */
export function setIfNotBlank(target, key, value) {
  if (!isBlank(value)) {
    target[key] = typeof value === 'string' ? value.trim() : value
  }
  return target
}

/** 多值參數：支援陣列與逗號字串（後端兩者都收） */
export function toMultiValue(value) {
  if (value === undefined || value === null) {
    return []
  }
  const list = Array.isArray(value) ? value : String(value).split(',')
  return list.map((item) => String(item).trim()).filter((item) => item !== '')
}

/** 分頁與排序（三個列表共用；非法值後端會回落，前端不自行校正） */
export function applyPaginateAndSort(query, filters, defaultSort) {
  query.page = filters.page && filters.page > 0 ? filters.page : 1
  const count = filters.count && filters.count > 0 ? Number(filters.count) : 20
  query.count = count > 200 ? 200 : count
  query.sort = filters.sort ? filters.sort : defaultSort
  query.direct = filters.direct === 'asc' ? 'asc' : 'desc'
  return query
}

/**
 * 匹配記錄列表（GET /api/film-circuit-ids）
 * @param {object} filters pos_id / pos_id_mode('prefix'|'exact') / circuit_id / film_id /
 *   film_master_id / status / manual_linked / has_special / with_audit_count / page / count / sort / direct
 */
export function buildFilmCircuitIdQuery(filters) {
  const f = filters || {}
  const query = {}

  // ⚠️ 空搜尋框：整個 key 都不送（送空串 = 後端回空集）
  if (!isBlank(f.pos_id)) {
    const mode = f.pos_id_mode === 'exact' ? 'exact' : 'prefix'
    query[mode === 'exact' ? 'pos_id_exact' : 'pos_id'] = String(f.pos_id).trim()
  }

  const circuits = toMultiValue(f.circuit_id)
  if (circuits.length > 0) {
    query.circuit_id = circuits
  }
  setIfNotBlank(query, 'film_id', f.film_id)
  setIfNotBlank(query, 'film_master_id', f.film_master_id)

  // status：預設 ''（只看有效行）；'*' = 全部（含 Deleted/Error）
  query.status = f.status === undefined || f.status === null ? '' : String(f.status)

  // 0/1 篩選：undefined = 不篩選（不是「等於 0」）
  setIfNotBlank(query, 'manual_linked', f.manual_linked)
  setIfNotBlank(query, 'has_special', f.has_special)
  if (f.with_audit_count === 1 || f.with_audit_count === true) {
    query.with_audit_count = 1
  }

  return applyPaginateAndSort(query, f, 'id')
}

/**
 * 白名單列表（GET /api/film-circuit-special-ids）
 * @param {object} filters circuit_id / pos_id / pos_id_mode / film_id / status / page / count / sort / direct
 */
export function buildSpecialIdQuery(filters) {
  const f = filters || {}
  const query = {}

  if (!isBlank(f.pos_id)) {
    const mode = f.pos_id_mode === 'exact' ? 'exact' : 'prefix'
    query[mode === 'exact' ? 'pos_id_exact' : 'pos_id'] = String(f.pos_id).trim()
  }

  const circuits = toMultiValue(f.circuit_id)
  if (circuits.length > 0) {
    query.circuit_id = circuits
  }
  setIfNotBlank(query, 'film_id', f.film_id)
  query.status = f.status === undefined || f.status === null ? '' : String(f.status)

  return applyPaginateAndSort(query, f, 'id')
}

/**
 * 變更記錄列表 / 匯出（GET /api/pos-id-audits[/export]）
 * 注意：這裡是 `pos_id_prefix=0` 切精確（不是 pos_id_exact）
 * @param {object} filters circuit_id / pos_id / pos_id_mode / film_id / film_master_id / action /
 *   source / change_type / operator / date_start / date_end / page / count / sort / direct
 */
export function buildPosIdAuditQuery(filters) {
  const f = filters || {}
  const query = {}

  if (!isBlank(f.pos_id)) {
    query.pos_id = String(f.pos_id).trim()
    if (f.pos_id_mode === 'exact') {
      query.pos_id_prefix = 0
    }
  }

  const circuits = toMultiValue(f.circuit_id)
  if (circuits.length > 0) {
    query.circuit_id = circuits.join(',')
  }
  setIfNotBlank(query, 'film_id', f.film_id)
  setIfNotBlank(query, 'film_master_id', f.film_master_id)

  const actions = toMultiValue(f.action)
  if (actions.length > 0) {
    query.action = actions.join(',')
  }
  const sources = toMultiValue(f.source)
  if (sources.length > 0) {
    query.source = sources.join(',')
  }
  setIfNotBlank(query, 'change_type', f.change_type)
  setIfNotBlank(query, 'operator', f.operator)
  setIfNotBlank(query, 'date_start', f.date_start)
  setIfNotBlank(query, 'date_end', f.date_end)

  return applyPaginateAndSort(query, f, 'id')
}

/**
 * 重定向映射列表 / 詳情（GET /api/manual-fix-mappings）
 * 契約：guide §5.1 —— 舊號/新號各有前綴與精確兩通道；空白規則同 §2.1（傳了但正規化後為空 → 空集）。
 * @param {object} filters from_pos_id / from_pos_id_mode('prefix'|'exact') / from_circuit_id /
 *   to_pos_id / to_pos_id_mode / from_title_name / date_start / date_end / page / count / sort / direct
 */
export function buildManualFixMappingQuery(filters) {
  const f = filters || {}
  const query = {}

  if (!isBlank(f.from_pos_id)) {
    const mode = f.from_pos_id_mode === 'exact' ? 'exact' : 'prefix'
    query[mode === 'exact' ? 'from_pos_id_exact' : 'from_pos_id'] = String(f.from_pos_id).trim()
  }
  if (!isBlank(f.to_pos_id)) {
    const mode = f.to_pos_id_mode === 'exact' ? 'exact' : 'prefix'
    query[mode === 'exact' ? 'to_pos_id_exact' : 'to_pos_id'] = String(f.to_pos_id).trim()
  }

  const circuits = toMultiValue(f.from_circuit_id)
  if (circuits.length > 0) {
    query.from_circuit_id = circuits
  }
  setIfNotBlank(query, 'from_title_name', f.from_title_name)
  setIfNotBlank(query, 'date_start', f.date_start)
  setIfNotBlank(query, 'date_end', f.date_end)

  return applyPaginateAndSort(query, f, 'created_at')
}

/** 刪除預覽的 query（guide §5.3：dry_run 走 ?dry_run=1，正式刪除不帶） */
export function buildMappingDeleteQuery(dryRun) {
  return dryRun === true ? { dry_run: 1 } : {}
}

/** 刪除的 body（guide §5.3/§5.4：reason 建議必填，會寫入 action=redirect / change_type=delete 的審計） */
export function buildMappingDeleteBody(reason) {
  const body = {}
  setIfNotBlank(body, 'reason', reason)
  return body
}

/** 新號台帳已不存在 → 這條映射已是死規則（guide §5.4-3，列表要標紅、可優先清理） */
export function isDeadMapping(row) {
  return Boolean(row) && (row.to_record === null || row.to_record === undefined)
}

/** 舊號仍有有效台帳（可能是雙向映射，guide §5.4-4，刪除前要提醒） */
export function hasFromLedger(row) {
  return Boolean(row) && row.from_ledger_exists === true
}

/**
 * 舊號剩餘票房的統一讀法（列表「舊號是否仍有票房」欄與刪除預覽共用）。
 * @returns {{finalsCount:number,gross:number,admissions:number,dateFrom:string,dateTo:string}}
 */
export function mappingEngagementSummary(engagements) {
  const e = engagements || {}
  const toNumber = (value) => {
    const num = Number(value)
    return isFinite(num) ? num : 0
  }
  return {
    finalsCount: toNumber(e.finals_count),
    gross: toNumber(e.finals_gross),
    admissions: toNumber(e.finals_admissions),
    dateFrom: isBlank(e.date_from) ? '' : String(e.date_from),
    dateTo: isBlank(e.date_to) ? '' : String(e.date_to)
  }
}

// ─────────────────────────────────────────────────────────────
// 3. 寫入 payload（guide §2.3 / §3.2 / §3.3）
// ─────────────────────────────────────────────────────────────

/**
 * 匹配記錄更新 payload（PUT /api/film-circuit-ids/{id}）
 * 只帶「有值」的欄位；`dry_run=true` 時後端只算不寫。
 * @param {object} form pos_id / film_id / film_master_id / title_name / sync_engagement_title_name /
 *   carry_over / date_start / date_end / create_mapping / force / reason
 * @param {boolean} dryRun
 */
export function buildFilmCircuitIdUpdatePayload(form, dryRun) {
  const f = form || {}
  const payload = {}

  setIfNotBlank(payload, 'pos_id', f.pos_id)
  setIfNotBlank(payload, 'film_id', f.film_id)
  setIfNotBlank(payload, 'film_master_id', f.film_master_id)
  if (!isBlank(f.title_name)) {
    payload.title_name = String(f.title_name)
  }

  if (f.sync_engagement_title_name === true) {
    payload.sync_engagement_title_name = true
  }
  if (f.carry_over === 'engagements') {
    payload.carry_over = 'engagements'
    // 日期範圍僅配合 carry_over 使用
    setIfNotBlank(payload, 'date_start', f.date_start)
    setIfNotBlank(payload, 'date_end', f.date_end)
  }
  if (f.create_mapping === true) {
    payload.create_mapping = true
  }
  if (f.force === true) {
    payload.force = true
  }
  setIfNotBlank(payload, 'reason', f.reason)
  payload.dry_run = dryRun === true

  return payload
}

/**
 * 白名單新增 payload（POST /api/film-circuit-special-ids）
 * @param {object} form circuit_id / pos_id / film_id / film_master_id / title_name / remarks /
 *   backfill_ledger / reason
 * @param {boolean} dryRun
 */
export function buildSpecialIdCreatePayload(form, dryRun) {
  const f = form || {}
  const payload = {}

  setIfNotBlank(payload, 'circuit_id', f.circuit_id)
  setIfNotBlank(payload, 'pos_id', f.pos_id)
  setIfNotBlank(payload, 'film_id', f.film_id)
  setIfNotBlank(payload, 'film_master_id', f.film_master_id)
  setIfNotBlank(payload, 'title_name', f.title_name)
  setIfNotBlank(payload, 'remarks', f.remarks)
  // 預設 true：該三元組在 film_circuit_ids 無行時補建一行台帳
  payload.backfill_ledger = f.backfill_ledger !== false
  setIfNotBlank(payload, 'reason', f.reason)
  payload.dry_run = dryRun === true

  return payload
}

/**
 * 白名單停用 / 啟用 payload（PUT /api/film-circuit-special-ids/{id}）：status 必須出現。
 *
 * ⚠️ 這個端點**不支援 dry_run**：控制器只驗證 `status` / `reason`，`dry_run` 會被靜默忽略，
 *    服務層一偵測到狀態不同就直接寫庫 + 寫審計（不是預演）。
 *    實測證據（2026-10-12，本機還原庫）：
 *      PUT /api/film-circuit-special-ids/2  {"status":"D","reason":"preview disable","dry_run":true}
 *      → 回應 changed=true、audit_id=3015865（不是 null），白名單狀態已被真的改掉。
 *    原始碼依據：hkbox-api app/Http/Controllers/API/FilmCircuitSpecialIdController.php:93-111
 *      （validate 只有 status / reason）與 app/Services/FilmCircuitSpecialIdService.php:414-463（toggleStatus 直接寫）。
 *    因此這裡**刻意不產生 dry_run 欄位**，避免「以為在預覽、其實已經改掉」。
 *    停用 / 啟用請改成：本地確認對話框（必填 reason；停用再加二次確認）→ 直接提交一次。
 *    對照：A3 `PUT film-circuit-ids` 與 B2 `POST film-circuit-special-ids` 都真的支援 dry_run。
 */
export function buildSpecialIdStatusPayload(status, reason) {
  const payload = {
    status: status === 'D' ? 'D' : ''
  }
  setIfNotBlank(payload, 'reason', reason)
  return payload
}

// ─────────────────────────────────────────────────────────────
// 4. 高影響操作與權限判斷
// ─────────────────────────────────────────────────────────────

/** 高影響操作 → 需二次確認（guide §2.3 步驟 2 / §5 通用規則 3） */
export function isHighImpactUpdate(form) {
  const f = form || {}
  return f.carry_over === 'engagements' || f.sync_engagement_title_name === true
}

/** 高影響操作的具體影響清單（給二次確認對話框逐條列出） */
export function highImpactReasons(form) {
  const f = form || {}
  const list = []
  if (f.carry_over === 'engagements') {
    list.push('連帶改掛歷史票房（engagement_finals / engagement_hourlies）的 title_key')
  }
  if (f.sync_engagement_title_name === true) {
    list.push('把新標題回寫到歷史票房記錄')
  }
  return list
}

/** 未匹配行（film_id = '0'，衝突加 Z 的產物） */
export function isUnmatchedRecord(row) {
  return row ? String(row.film_id) === '0' : false
}

// ─────────────────────────────────────────────────────────────
// 5. 顯示格式化
// ─────────────────────────────────────────────────────────────

/** 千分位數字（金額 / 人次 / 條數）；空值顯示 0 */
export function formatNumber(value) {
  if (isBlank(value)) {
    return '0'
  }
  const num = Number(value)
  if (!isFinite(num)) {
    return String(value)
  }
  return num.toLocaleString('en-US')
}

/** 後端 `notes[]` 一律逐條原樣展示（guide §5 通用規則 2） */
export function normalizeNotes(notes) {
  if (!Array.isArray(notes)) {
    return []
  }
  return notes.filter((note) => !isBlank(note)).map((note) => String(note))
}

/** 從 Content-Disposition 取檔名（支援 RFC 5987 的 filename*） */
export function parseContentDispositionFilename(header) {
  if (!header || typeof header !== 'string') {
    return ''
  }
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(header)
  if (utf8Match && utf8Match[1]) {
    const raw = utf8Match[1].trim().replace(/^"|"$/g, '')
    try {
      return decodeURIComponent(raw)
    } catch (e) {
      return raw
    }
  }
  const plainMatch = /filename="?([^";]+)"?/i.exec(header)
  return plainMatch && plainMatch[1] ? plainMatch[1].trim() : ''
}

/** 匯出用的兜底檔名：pos-id-audits_<start|all>-<end|all>_<YmdHis>.csv */
export function buildAuditCsvFallbackName(filters, date) {
  const f = filters || {}
  const start = isBlank(f.date_start) ? 'all' : String(f.date_start)
  const end = isBlank(f.date_end) ? 'all' : String(f.date_end)
  const d = date || new Date()
  const pad = (num) => String(num).padStart(2, '0')
  const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(
    d.getDate()
  )}${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
  return `pos-id-audits_${start}-${end}_${stamp}.csv`
}

/** 觸發瀏覽器下載（僅在瀏覽器內呼叫；本模組唯一的非純函式） */
export function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.style.display = 'none'
  link.href = url
  link.setAttribute('download', filename || 'download.csv')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// ─────────────────────────────────────────────────────────────
// 6. 篩選器選項（文案取自 guide §2.1 / §3.1 / §4.1；label 一律優先用後端回傳的 *_label）
// ─────────────────────────────────────────────────────────────

export const POS_ID_STATUS_OPTIONS = [
  { text: '只看有效（預設）', value: '' },
  { text: '全部（含 Deleted / Error）', value: '*' },
  { text: 'Deleted', value: 'Deleted' },
  { text: 'Error', value: 'Error' }
]

export const SPECIAL_ID_STATUS_OPTIONS = [
  { text: '只看有效（預設）', value: '' },
  { text: '全部（含已停用）', value: '*' },
  { text: '已停用', value: 'D' }
]

export const FILM_CIRCUIT_ID_SORT_OPTIONS = [
  { text: '記錄 id', value: 'id' },
  { text: 'pos-id', value: 'pos_id' },
  { text: '院線', value: 'circuit_id' },
  { text: '影片 id', value: 'film_id' },
  { text: '更新時間', value: 'updated_at' }
]

export const SPECIAL_ID_SORT_OPTIONS = [
  { text: '記錄 id', value: 'id' },
  { text: 'pos-id', value: 'pos_id' },
  { text: '院線', value: 'circuit_id' },
  { text: '影片 id', value: 'film_id' },
  { text: '建立時間', value: 'created_at' },
  { text: '更新時間', value: 'updated_at' }
]

export const MANUAL_FIX_MAPPING_SORT_OPTIONS = [
  { text: '記錄 id', value: 'id' },
  { text: '舊號', value: 'from_pos_id' },
  { text: '院線', value: 'from_circuit_id' },
  { text: '新號', value: 'to_pos_id' },
  { text: '建立時間', value: 'created_at' }
]

export const AUDIT_SORT_OPTIONS = [
  { text: '記錄 id', value: 'id' },
  { text: '時間', value: 'created_at' },
  { text: '院線', value: 'circuit_id' },
  { text: 'pos-id', value: 'pos_id' },
  { text: '影片 id', value: 'film_id' },
  { text: '匹配動作', value: 'action' },
  { text: '創建來源', value: 'source' },
  { text: '變動類型', value: 'change_type' },
  { text: '操作人', value: 'operator_name' }
]

/** 篩選器選項文案（表格渲染請直接用後端 `*_label`，不要自己映射） */
export const AUDIT_ACTION_OPTIONS = [
  { text: 'H 檔匹配', value: 'hourlies' },
  { text: 'F 檔匹配', value: 'final' },
  { text: '手動修改', value: 'manual' },
  { text: '白名單', value: 'special' },
  { text: '重定向', value: 'redirect' }
]

export const AUDIT_SOURCE_OPTIONS = [
  { text: 'H 檔 XML', value: 'hourlies_xml' },
  { text: 'F 檔 XML', value: 'final_xml' },
  { text: '手動創建', value: 'manual_create' },
  { text: '系統自動', value: 'auto_create' }
]

export const AUDIT_CHANGE_TYPE_OPTIONS = [
  { text: '新增', value: 'create' },
  { text: '修改', value: 'update' },
  // 2026-10-14 新增枚舉值：刪除生效規則（目前唯一來源＝刪除重定向映射，guide §4.1）
  { text: '删除', value: 'delete' }
]

/** create 綠 / update 藍 / delete 紅 */
export function auditChangeTypeVariant(changeType) {
  if (changeType === 'create') {
    return 'success'
  }
  if (changeType === 'update') {
    return 'primary'
  }
  if (changeType === 'delete') {
    return 'danger'
  }
  return 'secondary'
}

/** auto_create 灰底 + 「系統」角標（guide §5 組件建議） */
export function auditSourceVariant(source) {
  if (source === 'auto_create') {
    return 'secondary'
  }
  if (source === 'manual_create') {
    return 'info'
  }
  return 'light'
}
